import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ChartLineUp, Receipt, Coins, ShieldCheck, EnvelopeSimple,
  FileArrowDown, Robot, PlugsConnected,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";

const TOOLTIP_STYLE = {
  background: "#0A1311",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 10,
  color: "#F3F5F4",
  fontSize: 12,
};

const KPI_TILES = [
  { key: "receipts_captured",   label: "Receipts captured",   icon: Receipt,        color: "#FFB020" },
  { key: "certificates_issued", label: "Certificates issued", icon: ShieldCheck,    color: "#00A86B" },
  { key: "certificates_viewed", label: "Certificate views",   icon: ChartLineUp,    color: "#7AE1B3" },
  { key: "pdfs_downloaded",     label: "PDFs downloaded",     icon: FileArrowDown,  color: "#FFB020" },
  { key: "csv_exports",         label: "CSV exports",         icon: PlugsConnected, color: "#7AE1B3" },
  { key: "emails_sent",         label: "Emails sent",         icon: EnvelopeSimple, color: "#FFB020" },
  { key: "bank_imports",        label: "Bank imports",        icon: Coins,          color: "#00A86B" },
  { key: "advisor_messages",    label: "Advisor messages",    icon: Robot,          color: "#7AE1B3" },
];

export function KpiGrid({ kpis }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {KPI_TILES.map((k, i) => (
        <motion.div
          key={k.key}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: i * 0.04 }}
          className="glass rounded-xl p-5"
          data-testid={`analytics-kpi-${k.key}`}
        >
          <div className="flex items-start justify-between">
            <div className="eyebrow text-[10px]">{k.label}</div>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 bg-black/30">
              <k.icon size={15} weight="duotone" color={k.color} />
            </div>
          </div>
          <div className="mono text-2xl mt-3">{(kpis?.[k.key] ?? 0).toLocaleString()}</div>
        </motion.div>
      ))}
    </div>
  );
}

export function DailyActivityChart({ daily, days, totalEvents }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass rounded-xl p-6 lg:col-span-2"
      data-testid="chart-daily"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow">Daily activity</div>
          <div className="text-xs text-white/50 mt-1">
            {totalEvents.toLocaleString()} event{totalEvents === 1 ? "" : "s"} over last {days} days
          </div>
        </div>
        <ChartLineUp size={18} weight="duotone" color="#FFB020" />
      </div>
      <div className="h-64">
        <ResponsiveContainer>
          <AreaChart data={daily} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFB020" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#FFB020" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" fontSize={11} tickMargin={8} interval="preserveStartEnd" />
            <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} allowDecimals={false} width={30} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Area type="monotone" dataKey="count" stroke="#FFB020" strokeWidth={2} fill="url(#grad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}

export function EventTypeChart({ byType }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 }}
      className="glass rounded-xl p-6"
      data-testid="chart-by-type"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">Top event types</div>
        <ChartLineUp size={18} weight="duotone" color="#00A86B" />
      </div>
      {byType.length === 0 ? (
        <div className="text-white/45 text-xs py-8 text-center">No activity in this period.</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={byType} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" stroke="rgba(255,255,255,0.35)" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="label" stroke="rgba(255,255,255,0.6)" fontSize={10} width={130} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#00A86B" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}

export function TopVendors({ vendors }) {
  return (
    <div className="glass rounded-xl p-6" data-testid="top-vendors">
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">Top ERP integrations</div>
        <PlugsConnected size={18} weight="duotone" color="#FFB020" />
      </div>
      {(!vendors || vendors.length === 0) ? (
        <div className="text-white/45 text-xs py-8 text-center">No exports yet.</div>
      ) : (
        <div className="space-y-2">
          {vendors.map((v) => {
            const max = vendors[0].count || 1;
            const pct = (v.count / max) * 100;
            return (
              <div key={v.vendor} className="text-sm" data-testid={`vendor-${v.vendor}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="capitalize text-white/80">{v.vendor.replace(/_/g, " ")}</span>
                  <span className="mono text-white/60 text-xs">{v.count}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#00A86B,#FFB020)" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecentEventRow({ e }) {
  const metaLine = useMemo(() => (
    Object.entries(e.meta || {})
      .filter(([, v]) => v !== null && v !== "" && v !== undefined)
      .slice(0, 3)
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
      .join(" · ")
  ), [e.meta]);
  return (
    <tr>
      <td className="mono text-xs text-white/50">{e.timestamp?.slice(0, 19).replace("T", " ")}</td>
      <td>
        <span className="badge-pass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">
          {e.event_type}
        </span>
      </td>
      <td className="text-white/50 text-xs truncate max-w-xs">{metaLine}</td>
    </tr>
  );
}

export function RecentEvents({ recent }) {
  return (
    <div className="glass rounded-xl p-6 lg:col-span-2" data-testid="recent-events">
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">Recent activity</div>
        <span className="text-[10px] text-white/40 tracking-wider uppercase">Last {recent.length}</span>
      </div>
      {recent.length === 0 ? (
        <div className="text-white/45 text-xs py-8 text-center">Nothing yet — capture a receipt or issue a certificate.</div>
      ) : (
        <table className="data">
          <thead>
            <tr><th>When</th><th>Event</th><th>Details</th></tr>
          </thead>
          <tbody>
            {recent.map((e) => <RecentEventRow key={e.event_id} e={e} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}
